import * as Globalize from '@mapp/globalize';
import { LoginUser } from './loginUser';
import { Login, Token } from './login';
import { Blocked } from './blocked';
import { MissingTenant } from './tenant';
import { ID, CLDR } from '../const';
import { Templates, Template } from '../compile';
import { Activation } from './activation';
import { MvcApplication, Util } from 'mappMvc/models';
import { IView, IKeyValue } from 'mappMvc/interfaces';
import { View } from 'mappMvc/models/views';
import { CSS, ApplicationConfig } from 'mappMvc/const';
import { ViewOptions, ApplicationOptions } from 'mappMvc/interfaces/options';
import { MetadataClient } from 'mappClient/metadataClient';
import { registerValidation } from './extension/ko/ko-validation';
import { LocalizationClient } from 'mappClient/clients';
import { Localization, ILocalization } from 'mappMvc/compile';
import { GeoServiceType, FusionServiceType } from 'mappClient/enums/core';
import { ActionType } from 'mappClient/enums';
import { IToken } from 'mappClient/token';
import Theming from './utils/theming';
import { Branding } from './utils/branding';
import { SetupTwoFactorAuthentication } from './login/setuptwofactorauthentication';
import { IAvailableLocalization } from 'mappClient/models';

export abstract class StudioApplication extends MvcApplication {
	public login: Login;
	public studioSection: View;
	public user: LoginUser;

	private returnRoutes: string[];
	private blocked: Blocked;
	private missingTenant: MissingTenant;
	private tenant: string;
	private token: Token;
	private setupTwoFactorAuthentication: SetupTwoFactorAuthentication;

	abstract newStudioSection(): View;
	abstract getStorageId(): string;
	abstract getClientId(): string;
	abstract allowResetPassword(): boolean;
	requireTenant(): boolean {
		return true;
	}

	constructor(templates: any, template: any, globals?: IKeyValue<any>) {
		/* IE UserAgent */
		var doc = document.documentElement;
		doc.setAttribute('data-useragent', navigator.userAgent);
		window.mapp = window.mapp || {};

		globals = $.extend({}, globals, {
			ActionType: ActionType,
			GeoServiceType: GeoServiceType,
			FusionServiceType: FusionServiceType,
			Globalize: Globalize,
			ApplicationConfig: ApplicationConfig
		});

		super(
			ID.Application,
			template,
			<ViewOptions>{
				viewId: ID.Studio,
				template: Templates.layouts_section
			},
			globals
		);

		Theming.toggleThemeFiles();
		Branding.setBrandingColors();

		registerValidation();
	}

	//override
	public beforeInit(): Promise<any> {
		this.tenant = null;

		if (this.requireTenant()) {
			this.readTenantFromQuery();
		}

		MetadataClient.initialize(this.getStorageId(), this.tenant);
		MetadataClient.onRequestLogin = () => this.forceLogIn();
		MetadataClient.onBadRequest = () => this.badRequest();
		MetadataClient.onTokenRequest = () => this.tokenRequest();
		MetadataClient.onSetupTwoFactorRequest = (userId) => this.setupTwoFactorRequest(userId);
		MetadataClient.onTokenChanged = (token) => this.tokenChanged(token);

		//ko extensions - TODO Move
		ko.components.register('mapp-enterprise-studio', {
			viewModel: { instance: this },
			template: Template[Templates.mappEnterpriseStudio]
		});
		ko.validation.init({
			insertMessages: false,
			errorElementClass: CSS.Error
		});

		//i18n
		CLDR.forEach(cldrEntry => {
			Globalize.load(cldrEntry);
		});

		let locale =
			ApplicationConfig.language() ||
			(<any>window.navigator).userLanguage ||
			window.navigator.language;
		Globalize.locale(locale);

		ApplicationConfig.language.subscribe((language: string) => {
			Globalize.locale(language);
		});

		return super.beforeInit();
	}

	public async getLocalization(): Promise<ILocalization> {

		let localization = {} as ILocalization;

		let studio_storage = localStorage.getItem(this.getStorageId());

		let userLanguage = studio_storage ? JSON.parse(studio_storage).userLanguage : undefined;

		let available: IAvailableLocalization[] = [];

		const setDefault = async () => {
			try {
				localization = await LocalizationClient.getDefaultLocalization();
			} catch (e) {
			}
		}

		const setDefaultOrCompiled = async () => {
			try {
				if (userLanguage && userLanguage !== 'en') {
					await setDefault();
				}
			}
			catch (e) {
			}
		}

		try {
			await MetadataClient.isTokenValid();
		} catch (e) {
			await setDefault();
			return localization;
		}

		try {
			available = await LocalizationClient.getAvailableLocalizations();
			if (userLanguage && available.find(a => a.name === userLanguage)) {
				try {
					localization = await LocalizationClient.getStudioLocalization();
				}
				catch (e) {
					await setDefaultOrCompiled();
				}
			} else {
				await setDefaultOrCompiled();
			}
		} catch (error) {
			await setDefault();
		}

		return localization;
	}

	public getCldr(): Promise<any> {
		return new Promise((resolve, reject) => {
			MetadataClient.isTokenValid()
				.then(() => {
					LocalizationClient.getCldrLocalization().then((cldr: any) => {
						resolve(cldr);
					});
				})
				.catch(() => {
					LocalizationClient.getDefaultCldrLocalization().then((cldr: any) => {
						resolve(cldr);
					});
				});
		});
	}

	public getApplicationOptions(): Promise<ApplicationOptions> {
		this.user = new LoginUser(this);

		return new Promise<ApplicationOptions>((resolve, reject) => {
			/**
			 * Views
			 */
			let views: IView[] = [];

			//Login
			this.login = new Login({ parent: this });
			views.push(this.login);

			if (this.requireTenant()) {
				views.push(new Activation({ parent: this }));
			}

			this.blocked = new Blocked({ parent: this });
			views.push(this.blocked);

			this.missingTenant = new MissingTenant({ parent: this });
			views.push(this.missingTenant);

			this.token = new Token({ parent: this });
			views.push(this.token);

			this.setupTwoFactorAuthentication = new SetupTwoFactorAuthentication({ parent: this });
			views.push(this.setupTwoFactorAuthentication);

			MetadataClient.isTokenValid()
				.then(() => {
					let token: IToken = MetadataClient.getToken();
					if (token && token.userLanguage) {
						ApplicationConfig.language(token.userLanguage);
					} else {
						ApplicationConfig.language(ApplicationConfig.defaultLanguage);
					}
					//StudioSection
					this.studioSection = this.newStudioSection();
					views.push(this.studioSection);

					resolve(<ApplicationOptions>{
						defaultView: this.studioSection,
						views: views
					});
				})
				.catch(() => {
					ApplicationConfig.language(ApplicationConfig.defaultLanguage);

					resolve(<ApplicationOptions>{
						defaultView: this.login,
						views: views
					});
				});
		});
	}

	public forceLogIn() {
		let hash: string = window.location.hash;
		if (
			hash.indexOf(ID.Login) < 0 &&
			hash.indexOf(ID.Activation) < 0 &&
			hash.indexOf(ID.InitialCharge) < 0 &&
			hash.indexOf(ID.SetupTwoFactorAuthentication) < 0
		) {
			this.returnRoutes = [window.location.hash];
		}
		this.router.navigateToView(this.login);
	}

	private badRequest() {
		if (this.requireTenant()) {
			this.readTenantFromQuery();
			if (
				this.tenant == null ||
				MetadataClient.tenant == null ||
				this.tenant != MetadataClient.tenant
			) {
				MetadataClient.logout();
				this.router.navigateToView(this.missingTenant);
			}
		}
	}

	private tokenRequest() {
		this.token.setCredentials(this.login.name(), this.login.password());
		this.router.navigateToView(this.token);
	}

	private setupTwoFactorRequest(userId: string) {
		this.setupTwoFactorAuthentication.setUserId(userId);
		this.router.navigateToView(this.setupTwoFactorAuthentication);
	}

	private readTenantFromQuery() {
		var queryParams = Util.getQueryParams(location.search);

		if (!queryParams.tenant) {
			console.log(Localization.Errors.MissingTenant);
		}

		this.tenant = queryParams.tenant?.toLowerCase();
	}

	public routeNotFound() {
		return this.defaultView;
	}

	public loggedIn() {
		this.initLocalization()
			.then(() => this.initCldr())
			.then(() => this.getApplicationOptions())
			.then(options => {
				options.startAfterInit = false;
				return this.initApplication(options);
			})
			.then(() => this.proceedLogIn(this.user.updateUserInfo()));
	}

	protected proceedLogIn(updateUserInfoSuccess: boolean) {
		this.router.navigateToRoute(this.returnRoutes);
	}

	public block() {
		this.router.navigateToView(this.blocked);
	}

	private tokenChanged(token: IToken) {
		Branding.setBrandingColors();
	}
}

interface Window {
	studio: StudioApplication;
}

declare global {
	export interface Window {
		mapp: any;
	}
}

export function applyMixins(derivedCtor: any, baseCtors: any[]) {
	baseCtors.forEach(baseCtor => {
		Object.getOwnPropertyNames(baseCtor.prototype).forEach(name => {
			if (name !== 'constructor') {
				var descriptor = Object.getOwnPropertyDescriptor(baseCtor.prototype, name);
				Object.defineProperty(derivedCtor.prototype, name, descriptor);
			}
		});
	});
}
